/**
 * Angaben zur Person auf den öffentlichen Verteiler-Seiten (#388).
 *
 * Bis hierher wurde bei einer Selbstanmeldung **nur die Adresse** gespeichert. Im Pool
 * stand danach eine Zeile ohne Namen, und die Pflege filtert nach Organisation,
 * Kreisverband und Ortsverein — an dieser Person also vorbei. Genau darum ging es im
 * Issue: „sind ja teilweise nötig um die Verteiler zu filtern".
 *
 * Die Organisationsauswahl folgt dem Muster des Abfragelinks: Solange der Verband
 * gepflegt ist, wird ausgewählt; wer nicht in der Liste steht, tippt frei. Nichts davon
 * ist Pflicht außer dem Namen — eine erzwungene Verbandsangabe hielte Leute ab, die
 * schlicht auf dem Verteiler stehen wollen.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { lightTheme } from '../helfer/types';

const theme = lightTheme;

export interface Angaben {
  firstName: string;
  lastName: string;
  organization: string;
  kreisverband: string;
  ortsverein: string;
}

export const LEERE_ANGABEN: Angaben = {
  firstName: '', lastName: '', organization: '', kreisverband: '', ortsverein: '',
};

interface OrtsvereinKnoten { name: string }
interface KreisverbandKnoten { name: string; ortsvereine?: OrtsvereinKnoten[] }
interface OrgKnoten { name: string; kreisverbaende?: KreisverbandKnoten[] }

/** Wert, der die Auswahl auf ein Freitextfeld umschaltet. */
const ANDERE = '__andere__';

const feld: React.CSSProperties = {
  width: '100%', padding: '11px 13px', borderRadius: 'var(--r-m)', fontSize: '.9rem',
  border: `1px solid ${theme.border}`, background: theme.bgInput, color: theme.text,
  fontFamily: 'inherit', boxSizing: 'border-box',
  appearance: 'none' as React.CSSProperties['appearance'],
  WebkitAppearance: 'none' as React.CSSProperties['WebkitAppearance'],
};

const beschriftung: React.CSSProperties = {
  display: 'block', fontSize: '.78rem', color: theme.textMuted, marginBottom: 5,
};

const VerteilerAngaben: React.FC<{
  werte: Angaben;
  onChange: (werte: Angaben) => void;
}> = ({ werte, onChange }) => {
  const [orgs, setOrgs] = useState<OrgKnoten[]>([]);
  // Merkt sich je Ebene, ob gerade frei getippt wird. Sonst kippt das Feld beim Tippen
  // zurueck in die Auswahl, sobald der Wert nicht mehr auf einen Listeneintrag passt.
  const [frei, setFrei] = useState({ org: false, kv: false, ov: false });

  // Die Vorbelegung steht bewusst hier und nicht in einem zweiten Effekt: Sie gehoert
  // zum Eintreffen der Liste, und ein Effekt ueber `werte` liefe bei jedem Tastendruck.
  const werteRef = useRef(werte);
  werteRef.current = werte;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    axios.get('/api/public/organizations')
      .then(r => {
        const liste: OrgKnoten[] = r.data?.data ?? [];
        setOrgs(liste);
        // Gibt es genau eine Organisation, ist die Auswahl keine — dann steht sie schon
        // da, und der Kreisverband ist sofort waehlbar statt gesperrt.
        if (liste.length === 1 && !werteRef.current.organization) {
          onChangeRef.current({ ...werteRef.current, organization: liste[0].name });
        }
      })
      .catch(() => setOrgs([]));   // Ohne Liste bleiben es Freitextfelder.
  }, []);

  const gewaehlteOrg = useMemo(
    () => orgs.find(o => o.name === werte.organization) ?? null,
    [orgs, werte.organization],
  );
  const gewaehlterKV = useMemo(
    () => gewaehlteOrg?.kreisverbaende?.find(k => k.name === werte.kreisverband) ?? null,
    [gewaehlteOrg, werte.kreisverband],
  );

  const setze = (teil: Partial<Angaben>) => onChange({ ...werte, ...teil });



  /**
   * Ein vorhandener Wert, der **nicht** in der Liste steht, macht das Feld zum Freitext.
   *
   * Ohne das zeigte die Auswahl „Bitte waehlen …", obwohl ein Wert gespeichert ist — und
   * beim Speichern waere er still verschwunden. Das trifft jeden Wert aus einem Import,
   * der im gepflegten Verband nicht auftaucht (bemerkt im Browser mit „Kirchzarten").
   */
  const auswahl = (
    label: string,
    wert: string,
    optionen: string[],
    freiGewaehlt: boolean,
    umschalten: (an: boolean) => void,
    aendern: (v: string) => void,
    platzhalter: string,
    elternFehlt = false,
    elternHinweis = '',
  ) => {
    // Ein gespeicherter Wert, den die Liste nicht kennt, macht das Feld zum Freitext —
    // sonst zeigte die Auswahl „Bitte waehlen …" und der Wert waere beim Speichern weg.
    const wertPasstNicht = !!wert && !optionen.includes(wert);

    if (elternFehlt) {
      // Gesperrte Auswahl statt Textfeld: Die Abhaengigkeit soll man sehen, nicht raten.
      return (
        <div>
          <label style={beschriftung}>{label}</label>
          <select value="" disabled style={{ ...feld, color: theme.textMuted, cursor: 'not-allowed' }}>
            <option value="">{elternHinweis}</option>
          </select>
        </div>
      );
    }

    return (
      <div>
        <label style={beschriftung}>{label}</label>
        {optionen.length > 0 && !freiGewaehlt && !wertPasstNicht ? (
          <select
            value={wert}
            onChange={e => {
              if (e.target.value === ANDERE) { umschalten(true); aendern(''); }
              else aendern(e.target.value);
            }}
            style={feld}
          >
            <option value="">Bitte wählen …</option>
            {optionen.map(o => <option key={o} value={o}>{o}</option>)}
            <option value={ANDERE}>Andere …</option>
          </select>
        ) : (
          <input
            value={wert}
            onChange={e => aendern(e.target.value)}
            placeholder={platzhalter}
            style={feld}
          />
        )}
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 140px', minWidth: 0 }}>
          <label style={beschriftung}>Vorname</label>
          <input
            value={werte.firstName}
            onChange={e => setze({ firstName: e.target.value })}
            placeholder="Max"
            style={feld}
          />
        </div>
        <div style={{ flex: '1 1 140px', minWidth: 0 }}>
          <label style={beschriftung}>Nachname</label>
          <input
            value={werte.lastName}
            onChange={e => setze({ lastName: e.target.value })}
            placeholder="Mustermann"
            style={feld}
          />
        </div>
      </div>

      {auswahl(
        'Organisation (optional)',
        werte.organization,
        orgs.map(o => o.name),
        frei.org,
        an => setFrei(f => ({ ...f, org: an })),
        // Eine andere Organisation macht den bisherigen Kreisverband und Ortsverein
        // ungueltig — stehenlassen wuerde eine Zuordnung erzeugen, die es nicht gibt.
        v => setze({ organization: v, kreisverband: '', ortsverein: '' }),
        'z. B. DRK',
      )}

      {auswahl(
        'Kreisverband (optional)',
        werte.kreisverband,
        (gewaehlteOrg?.kreisverbaende ?? []).map(k => k.name),
        frei.kv,
        an => setFrei(f => ({ ...f, kv: an })),
        v => setze({ kreisverband: v, ortsverein: '' }),
        'z. B. Freiburg',
        // Ohne Organisation gibt es nichts zu waehlen. Steht dort eine, die die Liste
        // nicht kennt, wird frei getippt — dann kann auch der Kreisverband frei sein.
        orgs.length > 0 && !werte.organization,
        'Erst Organisation wählen …',
      )}

      {auswahl(
        'Ortsverein (optional)',
        werte.ortsverein,
        (gewaehlterKV?.ortsvereine ?? []).map(o => o.name),
        frei.ov,
        an => setFrei(f => ({ ...f, ov: an })),
        v => setze({ ortsverein: v }),
        'z. B. Kirchzarten',
        orgs.length > 0 && !werte.kreisverband,
        'Erst Kreisverband wählen …',
      )}
    </div>
  );
};

export default VerteilerAngaben;
